/**
 * Kids character band
 * Mirrors the behaviour of collection-list.js:
 *  - mobile  -> no Swiper, native draggable overflow scroll (.swipe-mobile)
 *  - desktop -> Swiper ONLY when the logos overflow the container, otherwise a static row
 * Extras: a compact progress bar that tracks both modes, autoplay that pauses on hover,
 * colour wash and dynamic CTA on hover/focus.
 */
if (!customElements.get('kids-character-band')) {
  class KidsCharacterBand extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.kids-band__items',
        nav: '.kids-band__nav',
        progressBar: '.kids-band__progress-bar',
        cta: '.section__header--buttons .btn',
        item: '.kids-char',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        navHidden: 'kids-band__nav--off',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`) || this;
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.nav = this.section.querySelector(this.selectors.nav);
      this.progressBar = this.nav ? this.nav.querySelector(this.selectors.progressBar) : null;

      if (!this.sliderWrapper) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotion;
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 3500;
      this.sliderInstance = false;

      this.bindInteractions();

      this.addEventListener('scroll', () => this.setProgress(), { passive: true });

      if (!window.FoxTheme || !window.FoxTheme.config) return;

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = this.init.bind(this);

      this.onResize = this.debounce(this.init.bind(this), 250);
      window.addEventListener('resize', this.onResize);

      this.init();
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        // Mobile: no Swiper. Native horizontal drag scroll via the theme .swipe-mobile styles.
        this.destroySlider();
        return;
      }

      if (this.hasOverflow()) {
        this.initSlider();
      } else {
        this.destroySlider();
      }
    }

    /* Only become a carousel when the logos actually exceed the available width. */
    hasOverflow() {
      const slides = Array.from(this.sliderWrapper.children).filter(
        (slide) => !slide.classList.contains('swiper-slide-duplicate')
      );
      if (slides.length < 2) return false;

      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--kids-gap')) || 20;
      const total = slides.reduce((sum, slide) => sum + slide.offsetWidth, 0) + gap * (slides.length - 1);

      return total > this.offsetWidth + 1;
    }

    initSlider() {
      if (typeof this.sliderInstance === 'object' && this.sliderInstance) {
        this.setProgress();
        return;
      }

      const sliderOptions = {
        slidesPerView: 'auto',
        spaceBetween: 12,
        threshold: 2,
        watchOverflow: true,
        speed: 600,
        loop: this.autoplayEnabled,
        loopAdditionalSlides: 2,
        navigation: false,
        pagination: false,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        autoplay: this.autoplayEnabled
          ? {
              delay: this.autoplaySpeed,
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
            }
          : false,
        breakpoints: {
          768: { spaceBetween: 20 },
          1280: { spaceBetween: 24 },
        },
      };

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);

      const modules = [];
      if (FoxTheme.Swiper.Mousewheel) modules.push(FoxTheme.Swiper.Mousewheel);
      if (this.autoplayEnabled && FoxTheme.Swiper.Autoplay) modules.push(FoxTheme.Swiper.Autoplay);

      this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, modules);
      this.sliderInstance.init();

      const slider = this.sliderInstance.slider;
      if (slider) {
        ['setTranslate', 'progress', 'slideChange', 'resize'].forEach((event) => {
          slider.on(event, () => this.setProgress());
        });
      }

      const focusableElements = FoxTheme.a11y.getFocusableElements(this);
      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          if (!slide || !this.sliderInstance) return;
          this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
        });
      });

      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          const index = Number(e.target.dataset.index);
          if (Number.isNaN(index) || !this.sliderInstance) return;
          this.sliderInstance.slider.slideTo(index);
        });
      }

      this.setProgress();
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);

      if (typeof this.sliderInstance === 'object' && this.sliderInstance) {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      this.setProgress();
    }

    /* One progress model for both modes: thumb width = visible share, offset = position. */
    setProgress() {
      if (!this.nav) return;

      let ratio = 1;
      let position = 0;

      if (this.sliderInstance && this.sliderInstance.slider) {
        const slider = this.sliderInstance.slider;
        const size = slider.virtualSize || (slider.wrapperEl ? slider.wrapperEl.scrollWidth : 0);
        if (size > 0 && slider.width > 0) {
          ratio = Math.min(1, slider.width / size);
          position = Math.min(1, Math.max(0, slider.progress || 0));
        }
      } else {
        const max = this.scrollWidth - this.clientWidth;
        if (max > 1) {
          ratio = Math.min(1, this.clientWidth / this.scrollWidth);
          position = Math.min(1, Math.max(0, this.scrollLeft / max));
        }
      }

      const scrollable = ratio < 0.999;
      this.nav.classList.toggle(this.classes.navHidden, !scrollable);
      if (!scrollable || !this.progressBar) return;

      const thumb = Math.max(ratio, 0.12);
      this.progressBar.style.width = `${thumb * 100}%`;
      this.progressBar.style.left = `${position * (1 - thumb) * 100}%`;
    }

    /* Hover / focus a character -> wash the band in its colour and retarget the CTA. */
    bindInteractions() {
      this.flood = this.dataset.flood === 'true';
      this.dynamicCta = this.dataset.dynamicCta === 'true';
      if (!this.flood && !this.dynamicCta) return;

      this.cta = this.section.querySelector(this.selectors.cta);
      this.ctaText = this.cta ? this.cta.querySelector('.btn__text') : null;
      this.ctaBaseLabel = this.ctaText ? this.ctaText.textContent : '';
      this.ctaBaseHref = this.cta ? this.cta.getAttribute('href') : null;

      // Delegated so Swiper's looped clones behave identically.
      this.addEventListener('mouseover', (e) => {
        const item = e.target.closest(this.selectors.item);
        if (item) this.apply(item);
      });
      this.addEventListener('focusin', (e) => {
        const item = e.target.closest(this.selectors.item);
        if (item) this.apply(item);
      });
      this.addEventListener('mouseleave', () => this.apply(null));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.apply(null);
      });
    }

    apply(item) {
      if (this.flood) {
        this.section.style.setProperty('--kids-flood', item ? item.dataset.tint : 'transparent');
      }

      if (!this.dynamicCta || !this.cta || !this.ctaText) return;

      if (item && item.dataset.ctaLabel && item.getAttribute('href')) {
        this.ctaText.textContent = item.dataset.ctaLabel;
        this.cta.setAttribute('href', item.getAttribute('href'));
      } else {
        this.ctaText.textContent = this.ctaBaseLabel;
        if (this.ctaBaseHref) this.cta.setAttribute('href', this.ctaBaseHref);
      }
    }
  }

  customElements.define('kids-character-band', KidsCharacterBand);
}
